import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/contexts/AuthContext";
import { useSellerOffers } from "@/hooks/usePropertyOffers";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OfferCard } from "@/components/seller/offers/OfferCard";
import { OfferDetailModal } from "@/components/seller/offers/OfferDetailModal";
import { Card, CardContent } from "@/components/ui/card";
import { Search, TrendingDown, TrendingUp, Clock } from "lucide-react";
import TourWrapper from "@/components/onboarding/TourWrapper";
import { sellerOffersSteps } from "@/config/tours/sellerOffersTour";
import { safeStorage } from "@/utils/safariStorage";
import type { PropertyOffer } from "@/types/buyer";

const SellerOffersPage = () => {
  usePageTitle("Property Offers");
  const { user } = useAuth();
  const { offers, stats, isLoading } = useSellerOffers(user?.id);
  const location = useLocation();
  
  const [selectedOffer, setSelectedOffer] = useState<PropertyOffer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "highest" | "lowest">("newest");
  const [activeTab, setActiveTab] = useState("pending");
  const [runTour, setRunTour] = useState(false);

  // Tour management
  useEffect(() => {
    const tourId = 'seller_offers';
    const hasSeenTour = safeStorage.getItem(`godyspo_tour_${tourId}`) === 'true';
    const shouldStartTour = location.state?.startTour === tourId;
    
    if (!hasSeenTour || shouldStartTour) {
      const checkAndStartTour = (retries = 0) => {
        const statsElement = document.querySelector('[data-tour="offers-stats"]');
        if (statsElement) {
          setTimeout(() => setRunTour(true), 500);
        } else if (retries < 10) {
          setTimeout(() => checkAndStartTour(retries + 1), 200);
        }
      };
      checkAndStartTour();
    }
  }, [location.state, offers.length]);

  const handleTourComplete = () => {
    safeStorage.setItem('godyspo_tour_seller_offers', 'true');
    setRunTour(false);
  };

  const handleTourSkip = () => {
    safeStorage.setItem('godyspo_tour_seller_offers', 'true');
    setRunTour(false);
  };

  // Filter offers by status
  const filterOffersByStatus = (status?: string) => {
    let filtered = offers;

    if (status && status !== "all") {
      filtered = filtered.filter(o => o.status === status);
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(o => 
        o.property?.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.property?.city?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    switch (sortBy) {
      case "highest":
        filtered = [...filtered].sort((a, b) => b.offer_amount - a.offer_amount);
        break;
      case "lowest":
        filtered = [...filtered].sort((a, b) => a.offer_amount - b.offer_amount);
        break;
      case "newest":
      default:
        filtered = [...filtered].sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }

    return filtered;
  };

  const handleViewDetails = (offer: PropertyOffer) => {
    setSelectedOffer(offer);
    setIsModalOpen(true);
  };

  const handleOfferUpdated = () => {
    // Offers will auto-refresh via real-time subscription
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Loading offers...</p>
      </div>
    );
  }

  // Helper to render offer cards with data-tour on first item only
  const renderOfferCards = (status?: string) => {
    const filteredOffers = filterOffersByStatus(status);
    
    if (filteredOffers.length === 0) {
      return (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">
              {status === 'pending' ? 'No pending offers' : 
               status === 'accepted' ? 'No accepted offers' :
               status === 'rejected' ? 'No rejected offers' :
               status === 'withdrawn' ? 'No withdrawn offers' : 'No offers yet'}
            </p>
          </CardContent>
        </Card>
      );
    }

    // Only add data-tour to first offer card in the active tab
    const isActiveTabContent = 
      (status === 'pending' && activeTab === 'pending') ||
      (status === undefined && activeTab === 'all') ||
      (status === 'accepted' && activeTab === 'accepted') ||
      (status === 'rejected' && activeTab === 'rejected') ||
      (status === 'withdrawn' && activeTab === 'withdrawn');

    return filteredOffers.map((offer, index) => (
      <div 
        key={offer.id}
        {...(index === 0 && isActiveTabContent ? { 'data-tour': 'offer-card' } : {})}
      >
        <OfferCard
          offer={offer}
          onViewDetails={() => handleViewDetails(offer)}
        />
      </div>
    ));
  };

  return (
    <>
      <TourWrapper
        steps={sellerOffersSteps}
        run={runTour}
        onComplete={handleTourComplete}
        onSkip={handleTourSkip}
      />
      
      <div className="container max-w-6xl mx-auto py-8 px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Property Offers</h1>
          <p className="text-muted-foreground">
            Manage offers received on your properties
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-tour="offers-stats">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Accepted</p>
              <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Withdrawn</p>
              <p className="text-2xl font-bold text-gray-600">{stats.withdrawn}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Sort Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6" data-tour="offers-search">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by property address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Newest First
                </div>
              </SelectItem>
              <SelectItem value="highest">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Highest Offer
                </div>
              </SelectItem>
              <SelectItem value="lowest">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4" />
                  Lowest Offer
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs for filtering by status */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex w-full overflow-x-auto" data-tour="offers-tabs">
            <TabsTrigger value="pending" className="flex-shrink-0">
              Pending
              {stats.pending > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                  {stats.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="flex-shrink-0">All</TabsTrigger>
            <TabsTrigger value="accepted" className="flex-shrink-0">Accepted</TabsTrigger>
            <TabsTrigger value="rejected" className="flex-shrink-0">Rejected</TabsTrigger>
            <TabsTrigger value="withdrawn" className="flex-shrink-0">Withdrawn</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {renderOfferCards('pending')}
          </TabsContent>

          <TabsContent value="all" className="space-y-4">
            {renderOfferCards()}
          </TabsContent>

          <TabsContent value="accepted" className="space-y-4">
            {renderOfferCards('accepted')}
          </TabsContent>

          <TabsContent value="rejected" className="space-y-4">
            {renderOfferCards('rejected')}
          </TabsContent>

          <TabsContent value="withdrawn" className="space-y-4">
            {renderOfferCards('withdrawn')}
          </TabsContent>
        </Tabs>

        {/* Offer Detail Modal */}
        <OfferDetailModal
          offer={selectedOffer}
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          onOfferUpdated={handleOfferUpdated}
        />
      </div>
    </>
  );
};

export default SellerOffersPage;
